import React from 'react';
import { FiBookOpen, FiUsers, FiTrendingUp, FiGlobe, FiZap, FiAward } from 'react-icons/fi';

const DifferentERPs = () => {
  const institutionTypes = [
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Schools (K-12)',
      stat: '1.5 million+ schools in India',
      description: 'Schools have their own rhythm. Daily attendance, parent-teacher meetings, report cards, extracurricular activities, and the constant need to keep parents informed. The focus is on holistic development, not just academics. Schools need systems that are simple enough for teachers to use without training, yet comprehensive enough to track every child\'s progress.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'PU Colleges (11th & 12th)',
      stat: '40,000+ PU colleges across India',
      description: 'Pre-university colleges are where specialization begins. Students choose streams—Science, Commerce, Arts—and start preparing for competitive exams. The academic calendar is different, the grading system is different, and the pressure on students is intense. PU colleges need systems that handle subject choices, stream management, and exam preparation tracking.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Degree Colleges',
      stat: '40,000+ degree colleges in India',
      description: 'Degree colleges operate at scale. Hundreds of courses, thousands of students, complex timetables, multiple exam patterns, and the challenge of managing attendance across semesters. Faculty are specialized, departments are distinct, and students need access to their academic records. The administrative complexity is significantly higher.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Universities',
      stat: '1,000+ universities in India',
      description: 'Universities are ecosystems. Multiple colleges under one umbrella, diverse courses, research programs, PhD scholars, international collaborations, and massive administrative structures. The needs range from managing affiliated colleges to handling research grants. Universities need systems that can handle complexity at scale—multiple departments, thousands of faculty, and tens of thousands of students.',
      color: 'bg-rose-50'
    }
  ];

  const commonChallenges = [
    {
      title: 'Different Academic Calendars',
      description: 'Schools start in June, colleges in July, universities have semester systems. Each has different exam schedules, holiday patterns, and admission cycles. A system that works for one fails miserably for another.',
      color: 'bg-blue-50'
    },
    {
      title: 'Different Grading Systems',
      description: 'Schools use percentages and grades. PU colleges follow a different pattern. Degree colleges have CGPA or SGPA systems. Universities have their own unique grading schemes. What works for one doesn\'t work for others.',
      color: 'bg-purple-50'
    },
    {
      title: 'Different Parent Expectations',
      description: 'Parents of school children want daily updates. Parents of college students want semester reports. Parents of university students care about placements and research. The communication needs are radically different.',
      color: 'bg-indigo-50'
    },
    {
      title: 'Different Reporting Requirements',
      description: 'Schools need report cards. Colleges need mark sheets. Universities need transcripts. Each requires different formats, different calculations, and different levels of detail. Schools are regulated by state boards. Colleges follow university regulations. Universities follow UGC guidelines. The compliance burden is different for each.',
      color: 'bg-amber-50'
    },
    {
      title: 'Different Staff Capabilities',
      description: 'School teachers need simple interfaces. College faculty need more advanced features. University professors need research management tools. The same interface can\'t serve all without confusing some and frustrating others.',
      color: 'bg-emerald-50'
    },
    {
      title: 'Different Technology Readiness',
      description: 'Some schools are still transitioning to digital. Colleges have some digital systems. Universities often have complex legacy systems. A single ERP needs to work across this entire spectrum of digital maturity.',
      color: 'bg-rose-50'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        In India, education is not a single journey—it's a journey that spans multiple types of institutions. From the first day of school to the final year of university, students pass through schools, PU colleges, degree colleges, and sometimes universities. Each stage has its own unique character, challenges, and needs.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For educational groups managing multiple institution types, the challenge is even more complex. <strong className="text-slate-900">Do you need a different ERP for each type of institution?</strong> Is that even practical? What does it cost to maintain separate systems? And is there a better way?
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Let's look at the reality of managing different institution types and why many trusts struggle with this question.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">1.5M+</div>
          <div className="text-sm text-slate-500">Schools across India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80,000+</div>
          <div className="text-sm text-slate-500">PU & Degree Colleges</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">1,000+</div>
          <div className="text-sm text-slate-500">Universities</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Understanding Each Institution Type</h2>

      {/* Institution Types */}
      {institutionTypes.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                <span className="bg-white/80 px-3 py-1 rounded-full text-xs font-bold text-amber-600 border border-amber-200">
                  {item.stat}
                </span>
              </div>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      <h2 className="text-2xl font-bold text-slate-900 mb-6 mt-8">The Common Challenges Across All Institution Types</h2>

      {/* Common Challenges */}
      {commonChallenges.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-5 mb-4 border`}>
          <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
          <p className="text-sm text-slate-600">{item.description}</p>
        </div>
      ))}

      {/* The Real Question Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Multi-Institution Dilemma</h3>
        <p className="text-slate-600 leading-relaxed mb-4">
          Educational trusts with schools, PU colleges, and degree colleges face a dilemma. 
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Option A: Separate ERPs</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>One ERP for schools, another for colleges</span></li>
              <li className="flex items-start gap-2">• <span>Data doesn't sync across institutions</span></li>
              <li className="flex items-start gap-2">• <span>Staff need training on multiple systems</span></li>
              <li className="flex items-start gap-2">• <span>Higher costs and implementation time</span></li>
              <li className="flex items-start gap-2">• <span>No central visibility across the trust</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent processes everywhere</span></li>
            </ul>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="text-sm font-semibold text-amber-700 mb-2">Option B: Force One ERP for All</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Ignore unique needs of each type</span></li>
              <li className="flex items-start gap-2">• <span>Features don't match requirements</span></li>
              <li className="flex items-start gap-2">• <span>Everyone forced into same template</span></li>
              <li className="flex items-start gap-2">• <span>Low adoption by teachers and staff</span></li>
              <li className="flex items-start gap-2">• <span>Frustrated users across all institutions</span></li>
              <li className="flex items-start gap-2">• <span>Eventually, system gets abandoned</span></li>
            </ul>
          </div>
        </div>
        <p className="text-slate-600 leading-relaxed mt-4">
          Neither option works. Separate ERPs create chaos and silos. A rigid system frustrates users and gets abandoned. <strong>Educational trusts need a third option.</strong>
        </p>
      </div>

      {/* The Truyon Solution */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Truyon's Approach: One Platform, Many Personalities</h3>
        <p className="text-indigo-800 leading-relaxed mb-3">
          Truyon understands that a school and a university are fundamentally different. They have different rhythms, different needs, and different users.
        </p>
        <p className="text-indigo-800 leading-relaxed mb-3">
          But they also share common foundations—students, teachers, attendance, grades, communication, and administration. 
        </p>
        <p className="text-indigo-800 leading-relaxed mb-3">
          <strong>What if you could have one platform that gives each institution exactly what it needs?</strong>
        </p>
        <div className="bg-white rounded-xl p-4 border border-indigo-200 mt-3">
          <ul className="space-y-2 text-sm text-indigo-800">
            <li className="flex items-start gap-2">• <span>Schools get simple interfaces and parent communication tools</span></li>
            <li className="flex items-start gap-2">• <span>PU Colleges get stream management and exam tracking</span></li>
            <li className="flex items-start gap-2">• <span>Degree Colleges get course management and semester handling</span></li>
            <li className="flex items-start gap-2">• <span>Universities get research management and affiliate college support</span></li>
          </ul>
        </div>
        <p className="text-indigo-800 leading-relaxed mt-3">
          One platform. Different experiences tailored to each institution type. <strong>Consistency where it matters, flexibility where it's needed.</strong>
        </p>
      </div>

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Why Truyon Works for All Institution Types</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
            <FiZap className="w-6 h-6 text-indigo-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Customizable for Each Type</h4>
            <p className="text-xs text-slate-600">Adapts to the unique needs of schools, colleges, and universities without forcing a one-size-fits-all approach.</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <FiTrendingUp className="w-6 h-6 text-emerald-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Consistent Core, Flexible Features</h4>
            <p className="text-xs text-slate-600">Shared foundation with specialized features for each institution type. Consistency across the trust, flexibility at the institution level.</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <FiGlobe className="w-6 h-6 text-amber-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Unified Central View</h4>
            <p className="text-xs text-slate-600">Track performance across all institution types from a single dashboard. Compare, analyze, and make informed decisions.</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">The Bottom Line</h3>
        <p className="text-indigo-800 leading-relaxed">
          Schools, PU colleges, degree colleges, and universities are different. They have different purposes, different rhythms, and different needs. Trying to force them into the same ERP template creates frustration and failure. Using separate ERPs creates chaos and silos.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>There is a better way.</strong> A platform that respects the uniqueness of each institution type while providing the consistency, visibility, and control that educational trusts need.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is that platform. One ERP that works for schools, PU colleges, degree colleges, and universities. Different experiences for different institutions. One unified platform for your entire trust.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Your institutions are diverse. Your ERP should be too.</strong>
        </p>
      </div>
    </>
  );
};

export default DifferentERPs;